/**
 * Hero Section Component
 * Landing section with branding and key benefits
 */

import React from 'react';
import { Calculator, TrendingUp, FileSpreadsheet, ArrowRight } from 'lucide-react';

export default function Hero({ onGetStarted }) {
  return (
    <div className="bg-gradient-to-br from-primary-50 via-white to-accent-50 border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left: Text Content */}
          <div className="space-y-6">
            {/* Logo + Badge */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <img
                src="/logo.svg"
                alt="SSP Calculator Logo"
                className="w-16 h-16 md:w-20 md:h-20"
              />
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-accent-100 border border-accent-300 rounded-full">
                <span className="w-2 h-2 bg-accent-500 rounded-full animate-pulse"></span>
                <span className="text-xs font-medium text-accent-800">April 2026 Ready</span>
              </div>
            </div>

            {/* Headline */}
            <div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-4 leading-tight">
                UK SSP Cost <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-accent-600">
                  Modeller
                </span>
              </h1>
              <p className="text-lg md:text-xl text-slate-600 leading-relaxed">
                Prepare for the <strong>Employment Rights Act 2024</strong>. Calculate and compare
                Statutory Sick Pay costs under current rules vs April 2026 changes.
              </p>
            </div>

            {/* Key Benefits */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <Calculator className="w-5 h-5 text-primary-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 text-sm">Individual Calculator</h3>
                  <p className="text-xs text-slate-600 mt-0.5">Single employee scenarios</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-5 h-5 text-accent-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 text-sm">Workforce Modelling</h3>
                  <p className="text-xs text-slate-600 mt-0.5">Cohort-based analysis</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                  <FileSpreadsheet className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 text-sm">Export & Share</h3>
                  <p className="text-xs text-slate-600 mt-0.5">Excel, PDF reports</p>
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <div className="flex flex-wrap items-center gap-4 pt-4">
              <button
                onClick={onGetStarted}
                className="btn-primary btn-lg group"
              >
                Get Started
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <div className="text-sm text-slate-600">
                <span className="font-medium">Free to use</span> • Built for UK employers
              </div>
            </div>
          </div>

          {/* Right: Illustration */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="/images/hero-illustration.png"
                alt="UK employer calculating SSP costs"
                className="w-full h-auto"
              />
            </div>

            {/* Floating Stats Cards */}
            <div className="hidden lg:block absolute -top-4 -left-4 bg-white rounded-xl shadow-lg p-4 border border-slate-200">
              <div className="text-2xl font-bold text-primary-600">£123.25</div>
              <div className="text-xs text-slate-600">2026 SSP Rate</div>
            </div>

            <div className="hidden lg:block absolute -bottom-4 -right-4 bg-white rounded-xl shadow-lg p-4 border border-slate-200">
              <div className="text-2xl font-bold text-accent-600">Day 1</div>
              <div className="text-xs text-slate-600">2026 Payment</div>
            </div>
          </div>
        </div>

        {/* Key Changes Banner */}
        <div className="mt-12 bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <span className="w-1 h-5 bg-gradient-to-b from-primary-500 to-accent-500 rounded"></span>
            Key April 2026 Changes
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <span className="text-green-600 font-bold">✓</span>
              </div>
              <div>
                <p className="font-medium text-slate-900">No Lower Earnings Limit</p>
                <p className="text-slate-600 text-xs mt-0.5">All employees qualify regardless of earnings</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <span className="text-green-600 font-bold">✓</span>
              </div>
              <div>
                <p className="font-medium text-slate-900">Day 1 Payment</p>
                <p className="text-slate-600 text-xs mt-0.5">No 3-day waiting period</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <span className="text-green-600 font-bold">✓</span>
              </div>
              <div>
                <p className="font-medium text-slate-900">Any Absence Length</p>
                <p className="text-slate-600 text-xs mt-0.5">No 4-day minimum requirement</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
